import type { MedicationForm } from "./types";

/**
 * Which procedure a surface shows, and which one only exists to be quoted.
 *
 * `howToTakeByForm` is keyed by the form of the medicine, and a factsheet picks
 * a key from the `form` her calendar records. Both halves of that live here
 * rather than inside the sheet that renders them: they are facts about the
 * catalogue, and a test has to be able to see them.
 */

/**
 * Every value of `MedicationForm`, as data.
 *
 * Built from a record rather than written as a list, so adding a member to the
 * union and forgetting it here is a type error rather than a silent gap in
 * the check below.
 */
const IS_A_FORM: Record<MedicationForm, true> = {
  pen: true,
  vial: true,
  pill: true,
  suppository: true,
  capsule: true,
  patch: true,
  pre_filled_syringe: true,
  lyophilized_powder: true,
  multi_vial: true,
};

export const MEDICATION_FORMS = Object.keys(IS_A_FORM) as MedicationForm[];

/**
 * The API's `form` value, translated to the content key holding its procedure.
 *
 * Moved out of `MedicationFactsheetSheet` so the rule below can be enforced
 * against it. Nothing else changed: the sheet imports it from here.
 */
export const API_FORM_TO_CONTENT_FORM: Record<string, MedicationForm> = {
  pre_filled_pen: "pen",
  pre_filled_syringe: "pre_filled_syringe",
  injection_solution: "vial",
  lyophilized_powder: "lyophilized_powder",
  multi_vial: "multi_vial",
  other: "vial",
};

/**
 * Keys under `howToTakeByForm` that are not a form and never render.
 *
 * `multidose` is the first, and it is there because of what the API cannot
 * say. Menopur's 600 and 1200 IU vials are mixed once and drawn from for up to
 * 28 days where its 75 and 150 IU vials are mixed fresh for each injection,
 * and the two procedures contradict each other at the end — one throws away
 * what is left in the vial, the other calls it the rest of her cycle. Nothing
 * stored tells them apart: same powder, same solvent, same "méthode
 * d'administration". The axis is printed on the glass and recorded nowhere,
 * which is why the coach asks her to read it and the factsheet cannot.
 *
 * So the block is the approved text the multidose walk's steps are matched
 * against, verbatim, by `menopur-protocol.test.ts` — and nothing else. A
 * patient reaches those steps through the coach or not at all. The fresh-mix
 * blocks she does read carry a sentence sending her there.
 *
 * **This is temporary and the test below is what will say so.** When
 * `patient_medications.presentation` lands — dashboard-api #1898, still open
 * and unmerged on 2026-09-30 — the answer she gives the coach is written back,
 * the factsheet reads it beside `form`, and `multidose` becomes an ordinary
 * key. The check fails that day on purpose: it asks whoever wires it up to
 * move the key deliberately, rather than to find out later that half a
 * procedure had been unreachable all along.
 */
export const SOURCE_ONLY_FORM_KEYS = ["multidose"] as const;

export type SourceOnlyFormKey = (typeof SOURCE_ONLY_FORM_KEYS)[number];

/** Whether a `howToTakeByForm` key is one a surface may render. */
export function rendersOnASurface(key: string): boolean {
  return (MEDICATION_FORMS as string[]).includes(key);
}
